import React from "react"
import { Linking, TouchableOpacity, View } from "react-native"
import { Body } from "@/components/standard/BFText"
import MyLeadsConnectView from "@/components/properties/myleads/MyLeadsConnectView"
import MyLeadsConnectCarouselAView from "@/components/properties/myleads/MyLeadsConnectCarouselAView"
import MyLeadsConnectCarouselBView from "@/components/properties/myleads/MyLeadsConnectCarouselBView"
import { type EmptyAnalysisListType } from "@/types/analysisTypes"
import { Image } from "expo-image"
import HeartIcon from "@/assets/icons/heart_vector.svg"
import { router } from "expo-router"
import { useStore } from "@nanostores/react"
import { $isMyLeadsRunning } from "@/stores/myLeads"
import House from "phosphor-react-native/src/icons/House"
import { BFColors } from "@/constants/Colors"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import ChartIcon from "@/assets/icons/chart-bar.svg"

interface EmptyAnalysisListProps {
  type: EmptyAnalysisListType
  variation?: "treatment_1" | "treatment_2" | "Control" // For carousel variants
}

const EmptyAnalysisList = ({ type, variation }: EmptyAnalysisListProps) => {
  const isMyLeadsRunning = useStore($isMyLeadsRunning)
  const isOnMyLeadsEmpty =
    type === "myLeads-buybox" ||
    type === "myLeads-nobuybox" ||
    type === "carousel"
  const isOnMyLeadsEmptyAndRunning = isOnMyLeadsEmpty && isMyLeadsRunning
  const isOnMyLeadsEmptyAndNotRunning = isOnMyLeadsEmpty && !isMyLeadsRunning

  if (type === "all" && !isMyLeadsRunning) {
    return (
      <View className="relative flex justify-center flex-1 px-6">
        <TouchableOpacity
          testID={"myleads-setup-link"}
          className={"flex items-center justify-center"}
          onPress={() => {
            router.push("/properties/myleads/connect")
          }}
        >
          <ChartIcon width={28} height={28} />
          <Body className="text-lightest text-center mt-3 mb-2">
            {"Start analyzing a property or "}
            <Body className="text-lightest underline">set up My Leads</Body>
            {" so you don’t miss out on a great deal."}
          </Body>
        </TouchableOpacity>

        <PrimaryDarkButton
          testID={"analyze-a-property-button"}
          title={"Analyze a Property"}
          onPress={() => {
            router.push("/analyze")
          }}
          className="mb-8 bottom-2 absolute"
          style={{ alignSelf: "center", width: 330 }}
        />
      </View>
    )
  }

  if (type === "carousel" && isOnMyLeadsEmpty) {
    if (variation === "treatment_1") {
      return <MyLeadsConnectCarouselAView />
    }
    if (variation === "treatment_2") {
      return <MyLeadsConnectCarouselBView />
    }
    return <MyLeadsConnectView />
  }

  if (isOnMyLeadsEmptyAndNotRunning) {
    return (
      <View className="flex items-center justify-center flex-1 px-6">
        <Image
          source={require("@/assets/images/eyes_1f440.png")}
          style={{ width: 36, height: 36 }}
        />
        <Body className="text-lightest text-center mt-3 mb-2">
          {
            "Uh-oh! We didn’t find any properties from\nyour Gmail account to sync. Check out this\narticle for sourcing leads."
          }
        </Body>
        <TouchableOpacity
          onPress={() => {
            void Linking.openURL("https://backflip.com/managing-your-leads/")
          }}
          testID="sourcing-article-link"
        >
          <Body className="text-teal underline mt-4">
            {"Sourcing for REIs"}
          </Body>
        </TouchableOpacity>
      </View>
    )
  } else if (isOnMyLeadsEmptyAndRunning) {
    return (
      <View className="flex items-center justify-center flex-1 px-6">
        <House color={BFColors.lightest} size={28} />
        <Body className="text-lightest text-center mt-3">
          {"We’re currently processing your\n" + "leads...almost there!"}
        </Body>
      </View>
    )
  }

  if (type === "favorites") {
    return (
      <View className="flex items-center justify-center flex-1">
        <HeartIcon width={28} height={28} />
        <Body className="text-lightest text-center mt-3">
          {
            "Favorite your property so you can quickly\nview your leads that you’re interested in!"
          }
        </Body>
      </View>
    )
  }

  return null
}

export default EmptyAnalysisList
